<template>
    <div class="flex w-full flex-col">
        <div class="w-full flex-grow">
            <h6
                v-if="label"
                class="relative"
            >
                {{ label }}
            </h6>
            <RadioGroup
                v-model="selected"
                :data-cy="vid"
                :disabled="disabled"
            >
                <RadioGroupLabel class="sr-only">{{ label }}</RadioGroupLabel>
                <div :class="vertical ? 'flex flex-col' : 'flex flex-wrap gap-x-6'">
                    <RadioGroupOption
                        v-for="item in selectItems"
                        :key="item.name"
                        v-slot="{ active, checked, disabled: disabledOption }"
                        :data-cy="`radio-option-${item.name}`"
                        as="div"
                        :value="item"
                        class="focus:outline-none"
                    >
                        <div
                            :class="[active ? 'ring-0' : '']"
                            class="relative flex cursor-pointer rounded-lg py-2 pr-2 outline-none focus:outline-none"
                        >
                            <div class="flex w-full items-center justify-between">
                                <div class="flex items-start">
                                    <div
                                        class="flex size-[18px] shrink-0 rounded-full border border-muval-gray-3 hover:border-muval-gray-2"
                                        :class="{ 'border-muval-gray-3': checked, 'bg-muval-gray-5': disabledOption }"
                                    >
                                        <div
                                            v-show="checked"
                                            class="m-auto h-2.5 w-2.5 rounded-full"
                                            :class="radioColor"
                                        ></div>
                                    </div>
                                    <RadioGroupLabel
                                        as="p"
                                        :class="[
                                            { 'text-gray-900 dark:text-gray-200': checked },
                                            { 'text-gray-900 dark:text-gray-200': !checked },
                                            { 'ml-2 text-sm': size === 'sm' },
                                            { 'ml-1 text-sm': size === 'base' },
                                            { 'ml-5 text-sm': size !== 'sm' },
                                        ]"
                                        class="font-normal"
                                    >
                                        <div class="flex flex-col">
                                            {{ item.name }}
                                            <div v-if="item.desc">
                                                <span class="text-sm font-light text-gray-500">{{ item.desc }}</span>
                                            </div>
                                        </div>
                                    </RadioGroupLabel>
                                </div>
                            </div>
                        </div>
                    </RadioGroupOption>
                </div>
            </RadioGroup>
        </div>
        <div
            v-if="errorMessage"
            class="my=auto mx-2 flex flex-shrink-0 text-center"
        >
            <div class="my-auto inline-flex text-xs text-error">
                <span class="my-auto">{{ errorMessage }}</span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { useField } from 'vee-validate';
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue';
import { idGen } from '@utilities/idGen';

const props = defineProps({
    classList: {
        type: [String, null],
        required: false,
        default: null,
    },
    selectItems: {
        type: [Array],
        required: false,
        default: () => [
            { name: 'Yes', value: 1 },
            { name: 'No', value: 0 },
        ],
    },
    label: {
        type: [String, null],
        required: false,
        default: null,
    },
    vid: {
        type: String,
        required: false,
        default: () => idGen(6),
    },
    size: {
        type: String,
        required: false,
        default: 'sm',
    },
    vertical: {
        type: Boolean,
        required: false,
        default: false,
    },
    modelValue: {
        type: [String, Number, Boolean, Array, Object],
        required: false,
        default: undefined,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    radioColor: {
        type: String,
        required: false,
        default: 'bg-black',
    },
});

const vid = toRef(props, 'vid');
const { value: inputValue, errorMessage } = useField(vid, undefined, {
    syncVModel: true,
});

const selected = computed({
    get: () => {
        if (!props.selectItems.length) {
            return;
        }
        return props.selectItems.find((item) => {
            if (inputValue.value === null) {
                return item.value === 0;
            }
            if (typeof props.modelValue === 'object') {
                return item.value.value === inputValue.value;
            }
            if (item.value === inputValue.value) {
                return item.value === inputValue.value;
            }
        });
    },
    set: (value) => {
        if (!value.value) {
            console.error('Looks like no value is set in selectItems @ MuvalRadioGroup');
        }
        inputValue.value = value.value;
    },
});
</script>
